import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, CheckCheck, ExternalLink, Star, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNotifications() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notification) => {
    if (notification.is_read) return;
    try {
      await axios.post(`${API_URL}/api/admin/notifications/${notification.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleViewInterview = async (notification) => {
    await markAsRead(notification);
    const interviewId = notification.interview_id || notification.metadata?.interview_id;
    if (interviewId) {
      navigate(`/admin/interviews?view=${interviewId}`);
    } else {
      navigate('/admin/interviews');
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
              <Bell className="w-6 h-6 text-lime-500" />
              Notifications
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-xs">{unreadCount} unread</Badge>
              )}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Interview completion alerts and system notifications</p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-page">
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all as read
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No notifications yet</p>
                <p className="text-sm mt-1">Notifications will appear here when students complete interviews</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer ${!n.is_read ? 'bg-lime-500/5' : ''}`}
                    onClick={() => handleViewInterview(n)}
                    data-testid={`notification-page-item-${n.id}`}
                  >
                    {/* Unread dot */}
                    <div className="mt-2 flex-shrink-0">
                      {!n.is_read ? (
                        <div className="w-3 h-3 rounded-full bg-lime-500" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-transparent border border-border" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="font-mono text-xs px-2">
                          {n.interview_number || 'INT'}
                        </Badge>
                        <Badge className={
                          n.ai_result === 'pass'
                            ? 'bg-green-500/20 text-green-500 border-green-500/30 text-xs'
                            : 'bg-red-500/20 text-red-500 border-red-500/30 text-xs'
                        }>
                          {n.ai_result?.toUpperCase()} {n.score ? `(${n.score}%)` : ''}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                      </div>
                      <p className="font-medium text-sm">
                        {n.student_name} completed their interview
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {n.university_name}
                      </p>
                    </div>

                    {/* Action */}
                    <div className="flex-shrink-0">
                      <Button variant="ghost" size="sm" className="text-xs h-8">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
